import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useOrg } from "@/lib/orgContext";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Send,
  Trash2,
  FileSignature,
  GitBranch,
  PenLine,
  ExternalLink,
  Lock,
  Info,
} from "lucide-react";
import { format } from "date-fns";
import { Link } from "wouter";
import { SignInAppDialog } from "./SignInAppDialog";
import { ApprovalReviewPanel, type FieldAnnotationDraft, type PriorAnnotation } from "./ApprovalReviewPanel";
import { ApprovalChecklistOverview } from "./ApprovalChecklistOverview";
import { useUser } from "@clerk/react";

function AuthedImage({ url, getToken, alt, className }: { url: string; getToken: () => Promise<string | null>; alt: string; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(objectUrl);
      } catch { /* ignore */ }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, getToken]);
  if (!src) return <div className={`bg-muted/40 rounded ${className ?? "h-16 w-40"}`} />;
  return <img src={src} alt={alt} className={className} />;
}

async function openAuthed(url: string, getToken: () => Promise<string | null>, opts?: { download?: string }) {
  const token = await getToken();
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return;
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  if (opts?.download) {
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = opts.download;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(objUrl, "_blank", "noopener,noreferrer");
  }
  setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
}

interface RouteStage {
  stage:
    | "finance_review"
    | "hr_review"
    | "sport_admin_review"
    | "ad_review"
    | "compliance_review"
    | "legal_review"
    | "signature";
  approvers: string[];
  quorum: "all" | "any";
  skipped?: boolean;
  skipReason?: string;
  /** Task #380: persona this stage was auto-routed to. */
  routedPersona?: "Finance" | "HR" | "Legal" | "SA";
  /** Task #380: human-readable routing decision (or skip reason). */
  routingReason?: string;
}

interface Approval {
  id: number;
  contractId: number;
  isAmendment: boolean;
  currentStage: string;
  status: string;
  resolvedRoute: RouteStage[];
  submittedBy: string | null;
  submittedAt: string | null;
  completedAt: string | null;
}

interface Step {
  id: number;
  approvalId: number;
  stage: string;
  approverEmail: string;
  decision: string | null;
  comment: string | null;
  annotations?: {
    field: string;
    fieldLabel?: string;
    comment: string;
    resolvedAt?: string | null;
    replies?: {
      authorEmail: string;
      authorUserId?: string;
      comment: string;
      createdAt: string;
      editedAt?: string;
    }[];
  }[] | null;
  decidedAt: string | null;
  createdAt: string;
}

interface Signer {
  email: string;
  name?: string;
  status: "pending" | "sent" | "signed" | "declined";
  signedAt?: string;
  method?: "in_app_draw" | "in_app_typed" | "external";
  signedImageKey?: string;
  typedName?: string;
  typedFont?: string;
  consentIp?: string;
  consentUserAgent?: string;
}

interface Signature {
  id: number;
  approvalId: number;
  providerEnvelopeId: string;
  signers: Signer[];
  status: string;
  sentAt: string | null;
  completedAt: string | null;
  signedPdfKey?: string | null;
}

interface SignerSession {
  signature: Signature | null;
  yourSigner: Signer | null;
  allowedSigningMethods: "external_only" | "in_app_only" | "both";
}

const METHOD_LABEL: Record<string, string> = {
  in_app_draw: "In-app · Drawn",
  in_app_typed: "In-app · Typed",
  external: "External provider",
};

interface ApprovalState {
  approval: Approval | null;
  steps: Step[];
  /** Steps from previous approval cycles (e.g. before a return-for-changes). */
  priorSteps?: Step[];
  signature: Signature | null;
  previewRoute: RouteStage[] | null;
  history: Approval[];
}

const STAGE_LABEL: Record<string, string> = {
  draft: "Draft",
  finance_review: "Finance Review",
  hr_review: "HR Review",
  sport_admin_review: "Sport Admin Review",
  legal_review: "Legal Review",
  compliance_review: "Compliance Review",
  ad_review: "AD Review",
  signature: "Signature",
  executed: "Executed",
};

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  in_progress: "secondary",
  executed: "default",
  rejected: "destructive",
  returned: "outline",
  cancelled: "outline",
};

interface ApprovalSectionProps {
  contractId: number;
  /**
   * Optional callback invoked after every internal refresh of approval
   * state (initial load, after a decision/submit/cancel/sign action).
   * Parents that maintain their own approval snapshot (e.g. the
   * term-sheet detail page, which gates "Mark Internally Approved" on
   * `approvalStatus.isExecuted`) should pass a callback that re-fetches
   * the snapshot so their gating CTAs update live without requiring
   * the user to reload the page.
   */
  onApprovalChange?: () => void;
}

export function ApprovalSection({ contractId, onApprovalChange }: ApprovalSectionProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const { org } = useOrg();
  const { user } = useUser();
  const currentUserEmail = user?.primaryEmailAddress?.emailAddress ?? null;
  const isAdmin = org?.role === "admin";
  const [state, setState] = useState<ApprovalState | null>(null);
  const [loading, setLoading] = useState(true);
  const [decisionComment, setDecisionComment] = useState("");
  const [decisionPending, setDecisionPending] = useState<string | null>(null);
  const [signerEmail, setSignerEmail] = useState("");
  const [signerName, setSignerName] = useState("");
  const [session, setSession] = useState<SignerSession | null>(null);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [draftAnnotations, setDraftAnnotations] = useState<FieldAnnotationDraft[]>([]);
  const [contract, setContract] = useState<Record<string, unknown> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const token = await getToken();
      // Run both fetches in parallel but isolate failures so a flaky
      // contract fetch can't prevent the approval state (the primary data)
      // from loading.
      const [approvalRes, contractRes] = await Promise.allSettled([
        fetch(`/api/contracts/${contractId}/approval`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/contracts/${contractId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (contractRes.status === "fulfilled" && contractRes.value.ok) {
        try {
          setContract((await contractRes.value.json()) as Record<string, unknown>);
        } catch { /* ignore */ }
      }
      if (approvalRes.status !== "fulfilled") return;
      const res = approvalRes.value;
      if (res.ok) {
        const data = (await res.json()) as ApprovalState;
        setState(data);
        if (data.approval) {
          try {
            const sres = await fetch(`/api/approvals/${data.approval.id}/signature/session`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (sres.ok) setSession((await sres.json()) as SignerSession);
            else setSession(null);
          } catch {
            setSession(null);
          }
        } else {
          setSession(null);
        }
      }
    } finally {
      setLoading(false);
      // Notify parents so they can re-fetch any approval-derived state
      // (lifecycle gates, etc.) without polling.
      onApprovalChange?.();
    }
  }, [getToken, contractId, onApprovalChange]);

  useEffect(() => { refresh(); }, [refresh]);

  async function callApi(path: string, init: RequestInit = {}) {
    const token = await getToken();
    const res = await fetch(path, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { error?: string }).error ?? `Error ${res.status}`);
    return data;
  }

  async function handleDecision(decision: "approved" | "rejected" | "request_changes") {
    if (!state?.approval) return;
    if ((decision === "rejected" || decision === "request_changes") && !decisionComment.trim()) {
      toast({ title: "Comment required", description: "Please provide a comment.", variant: "destructive" });
      return;
    }
    setDecisionPending(decision);
    try {
      await callApi(`/api/approvals/${state.approval.id}/decision`, {
        method: "POST",
        body: JSON.stringify({
          decision,
          comment: decisionComment.trim() || undefined,
          annotations: draftAnnotations.length > 0 ? draftAnnotations : undefined,
        }),
      });
      toast({
        title: `Decision recorded`,
        description: draftAnnotations.length > 0
          ? `You ${decision.replace("_", " ")} this stage with ${draftAnnotations.length} inline note${draftAnnotations.length === 1 ? "" : "s"}.`
          : `You ${decision.replace("_", " ")} this stage.`,
      });
      setDecisionComment("");
      setDraftAnnotations([]);
      await refresh();
    } catch (err) {
      toast({ title: "Decision failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    } finally {
      setDecisionPending(null);
    }
  }

  async function handleAddSigner() {
    if (!state?.approval || !signerEmail.includes("@")) {
      toast({ title: "Valid email required", variant: "destructive" });
      return;
    }
    try {
      await callApi(`/api/approvals/${state.approval.id}/signature/signers`, {
        method: "POST",
        body: JSON.stringify({ email: signerEmail.toLowerCase(), name: signerName || undefined }),
      });
      setSignerEmail("");
      setSignerName("");
      await refresh();
    } catch (err) {
      toast({ title: "Add signer failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    }
  }

  async function handleRemoveSigner(email: string) {
    if (!state?.approval) return;
    try {
      await callApi(`/api/approvals/${state.approval.id}/signature/signers/${encodeURIComponent(email)}`, {
        method: "DELETE",
      });
      await refresh();
    } catch (err) {
      toast({ title: "Remove failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    }
  }

  async function handleSendEnvelope() {
    if (!state?.approval) return;
    try {
      await callApi(`/api/approvals/${state.approval.id}/signature/send`, { method: "POST" });
      toast({ title: "Sent for signature", description: "Signers have been notified." });
      await refresh();
    } catch (err) {
      toast({ title: "Send failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    }
  }

  async function handleSimulateSign(email: string) {
    if (!state?.approval) return;
    try {
      await callApi(`/api/approvals/${state.approval.id}/signature/simulate-sign`, {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      await refresh();
    } catch (err) {
      toast({ title: "Sign failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    }
  }

  if (loading) return <Skeleton className="h-48 w-full" />;
  if (!state) return null;

  const route = state.approval?.resolvedRoute ?? state.previewRoute ?? [];
  const currentStage = state.approval?.currentStage ?? "draft";
  const isLocked = state.approval?.currentStage === "signature" && state.approval?.status === "in_progress";
  const isExecuted = state.approval?.status === "executed";
  const isReviewStage =
    state.approval?.status === "in_progress" &&
    currentStage !== "signature" &&
    currentStage !== "draft" &&
    currentStage !== "executed";

  return (
    <div className="space-y-6">
      {/* Locked banner */}
      {isLocked && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900">
          <Lock className="h-4 w-4 text-amber-700" />
          <AlertDescription className="font-medium">
            This contract is locked pending e-signature. Field and clause edits are disabled until all parties sign or the approval cycle is reset.
          </AlertDescription>
        </Alert>
      )}

      {/* Executed banner */}
      {isExecuted && (
        <Alert className="border-green-300 bg-green-50 text-green-900">
          <CheckCircle2 className="h-4 w-4 text-green-700" />
          <AlertDescription className="font-medium">
            This contract has been fully executed. All approvals and signatures are complete.
          </AlertDescription>
        </Alert>
      )}

      {/* High-level checklist — one row per stage with required approvers,
          decisions, timestamps and inline comments. The detail card below
          remains the workhorse for actions; this overview is the single
          best "where are we?" surface for everyone reviewing. */}
      <ApprovalChecklistOverview
        route={route}
        steps={state.steps}
        currentStage={state.approval?.currentStage ?? null}
        approvalStatus={state.approval?.status ?? null}
        currentUserEmail={currentUserEmail}
      />

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="h-5 w-5" /> Approval Workflow
              </CardTitle>
              <CardDescription>
                {state.approval
                  ? <>Stage: <strong>{STAGE_LABEL[currentStage]}</strong> · Status: <Badge variant={STATUS_VARIANT[state.approval.status] ?? "secondary"} className={`ml-1 ${state.approval.status === "executed" ? "bg-green-600 hover:bg-green-600" : ""}`}>{state.approval.status.replace(/_/g, " ")}</Badge>
                      {state.approval.isAmendment && <Badge variant="outline" className="ml-2">Amendment</Badge>}
                    </>
                  : "No approval started — preview shows the route this contract would take."}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Route visualization */}
          {route.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {route.map((stage, idx) => {
                // Compare positions within the resolved route itself rather
                // than against a hardcoded stage list — this stays correct
                // regardless of which optional stages (e.g. compliance) the
                // org has enabled.
                const currentIdx = route.findIndex((r) => r.stage === currentStage);
                const isCurrent = state.approval?.currentStage === stage.stage;
                const isPast = state.approval !== null && currentIdx >= 0 && idx < currentIdx;
                return (
                  <div key={stage.stage} className="flex items-center gap-2">
                    <div className={`px-3 py-1.5 rounded-md text-sm border flex items-center gap-1.5 ${
                      stage.skipped
                        ? "bg-muted text-muted-foreground line-through"
                        : isCurrent
                        ? "bg-primary text-primary-foreground border-primary"
                        : isPast
                        ? "bg-green-50 border-green-200 text-green-800"
                        : "bg-background text-muted-foreground"
                    }`}>
                      {isPast && !stage.skipped && <CheckCircle2 className="h-3.5 w-3.5" />}
                      {STAGE_LABEL[stage.stage]}
                      {stage.approvers.length > 0 && !stage.skipped && (
                        <span className="text-xs opacity-70">
                          ({stage.quorum === "all" ? "all" : "any"} of {stage.approvers.length})
                        </span>
                      )}
                    </div>
                    {idx < route.length - 1 && <span className="text-muted-foreground text-xs">→</span>}
                  </div>
                );
              })}
              {state.approval?.currentStage === "executed" && (
                <>
                  <span className="text-muted-foreground text-xs">→</span>
                  <Badge className="bg-green-600 hover:bg-green-600">Executed</Badge>
                </>
              )}
            </div>
          )}

          {state.approval?.submittedAt && (
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5" />
              Submitted {format(new Date(state.approval.submittedAt), "MMM d, yyyy h:mm a")}
              {state.approval.submittedBy && <> by <span className="font-mono">{state.approval.submittedBy}</span></>}
            </div>
          )}

          {/* Active stage decision panel (for approvers) */}
          {isAdmin && isReviewStage && (
            <>
              <Separator />
              {/* Redline: changes included in this submission (since previous submission) */}
              <div className="space-y-2">
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  onClick={() => setShowChanges((v) => !v)}
                >
                  {showChanges ? "▼" : "▶"} {showChanges ? "Hide" : "Show"} changes included in this submission
                </button>
                {showChanges && (
                  <ApprovalReviewPanel
                    contractId={contractId}
                    submittedAt={state.approval?.submittedAt ?? null}
                    previousSubmittedAt={
                      state.history.length > 1
                        ? (state.history[1]?.submittedAt ?? null)
                        : null
                    }
                    annotations={draftAnnotations}
                    onAnnotationsChange={setDraftAnnotations}
                    contract={contract}
                    approvalId={state.approval?.id ?? null}
                    approvalStatus={state.approval?.status ?? null}
                    onReplyPosted={refresh}
                    priorAnnotations={[
                      ...(state.priorSteps ?? []),
                      ...state.steps,
                    ].flatMap<PriorAnnotation>((s) =>
                      (s.annotations ?? [])
                        .map((a, idx) => ({
                          field: a.field,
                          fieldLabel: a.fieldLabel,
                          comment: a.comment,
                          approverEmail: s.approverEmail,
                          decidedAt: s.decidedAt,
                          stage: s.stage,
                          stepId: s.id,
                          annotationIndex: idx,
                          replies: a.replies,
                          resolvedAt: a.resolvedAt ?? null,
                        }))
                        .filter((a) => !a.resolvedAt),
                    )}
                  />
                )}
              </div>
              <Separator />
              <div className="space-y-3">
                <div className="font-medium text-sm">Record decision for {STAGE_LABEL[currentStage]}</div>
                {state.approval && (
                  <>
                    {(() => {
                      const cur = route.find((s) => s.stage === currentStage);
                      if (!cur) return null;
                      return (
                        <div className="space-y-1">
                          {cur.routedPersona && (
                            <div className="flex items-center gap-2 text-xs">
                              <Badge variant="secondary" data-testid={`approval-routed-persona-${cur.stage}`}>
                                Routed to {cur.routedPersona}
                              </Badge>
                              {cur.routingReason && (
                                <span className="text-muted-foreground">{cur.routingReason}</span>
                              )}
                            </div>
                          )}
                          {cur.approvers.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              Approvers for this stage: {cur.approvers.join(", ")}
                              {cur.quorum === "any" && cur.approvers.length > 1 && (
                                <span className="ml-1">(any one can approve)</span>
                              )}
                            </div>
                          )}
                          {cur.approvers.length === 0 && cur.routedPersona && (
                            <div
                              className="text-xs text-amber-700"
                              data-testid={`approval-no-persona-holders-${cur.stage}`}
                            >
                              No {cur.routedPersona} persona-holders in this org — falling back to admin approval.
                              {isAdmin && (
                                <>
                                  {" "}
                                  <Link
                                    href="/settings#team"
                                    className="underline font-medium hover:no-underline"
                                    data-testid={`approval-assign-persona-link-${cur.stage}`}
                                  >
                                    Assign a {cur.routedPersona} reviewer to route automatically →
                                  </Link>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
                <Textarea
                  placeholder="Comment (required for reject / request changes)"
                  value={decisionComment}
                  onChange={(e) => setDecisionComment(e.target.value)}
                  className="min-h-[80px]"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => handleDecision("approved")}
                    disabled={decisionPending !== null}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {decisionPending === "approved" ? "Approving…" : "Approve"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDecision("request_changes")}
                    disabled={decisionPending !== null}
                  >
                    <AlertCircle className="h-4 w-4 mr-2" />
                    {decisionPending === "request_changes" ? "Sending…" : "Request Changes"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDecision("rejected")}
                    disabled={decisionPending !== null}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {decisionPending === "rejected" ? "Rejecting…" : "Reject"}
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Drafter view: when the contract is back with the drafter (returned
              for changes or rejected), surface inline notes from earlier review
              cycles + allow replies. */}
          {(state.approval?.status === "returned" ||
            state.approval?.status === "rejected") && !isReviewStage && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium">Reviewer notes from this cycle</div>
                <div className="text-xs text-muted-foreground">
                  Reply to any approver note inline. Replies are saved to the
                  original note and shown to the next approver.
                </div>
                <ApprovalReviewPanel
                  contractId={contractId}
                  submittedAt={state.approval?.submittedAt ?? null}
                  previousSubmittedAt={
                    state.history.length > 1
                      ? (state.history[1]?.submittedAt ?? null)
                      : null
                  }
                  contract={contract}
                  approvalId={state.approval?.id ?? null}
                  approvalStatus={state.approval?.status ?? null}
                  onReplyPosted={refresh}
                  priorAnnotations={[
                    ...(state.priorSteps ?? []),
                    ...state.steps,
                  ].flatMap<PriorAnnotation>((s) =>
                    (s.annotations ?? [])
                      .map((a, idx) => ({
                        field: a.field,
                        fieldLabel: a.fieldLabel,
                        comment: a.comment,
                        approverEmail: s.approverEmail,
                        decidedAt: s.decidedAt,
                        stage: s.stage,
                        stepId: s.id,
                        annotationIndex: idx,
                        replies: a.replies,
                        resolvedAt: a.resolvedAt ?? null,
                      }))
                      .filter((a) => !a.resolvedAt),
                  )}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Signature panel — shown when in signature stage OR signature exists */}
      {state.approval && (state.approval.currentStage === "signature" || state.signature) && (
        <Card className={isLocked ? "border-amber-300" : undefined}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" />
              {isLocked && <Lock className="h-4 w-4 text-amber-700" />}
              E-Signature
            </CardTitle>
            <CardDescription>
              {isLocked
                ? "All approvals are complete. Add signatories and send for signature to finalize the contract."
                : "Signature collection for this contract."}
              {state.signature && (
                <> · Envelope: <span className="font-mono text-xs">{state.signature.providerEnvelopeId}</span>
                 · Status: <Badge variant="outline" className="ml-1">{state.signature.status}</Badge></>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {state.signature && state.signature.signers.length > 0 && (
              <div className="space-y-2">
                {state.signature.signers.map((s) => {
                  const isYou = session?.yourSigner?.email.toLowerCase() === s.email.toLowerCase();
                  const allowed = session?.allowedSigningMethods ?? "both";
                  const canSignInApp =
                    isYou &&
                    s.status !== "signed" &&
                    s.status !== "declined" &&
                    allowed !== "external_only" &&
                    state.signature?.status !== "completed" &&
                    state.signature?.status !== "declined";
                  return (
                    <div key={s.email} className="flex items-start justify-between py-2 px-3 border rounded-md gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium flex items-center gap-2">
                          {s.name ?? s.email}
                          {isYou && <Badge variant="outline" className="text-[10px]">you</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground">{s.email}</div>
                        {s.status === "signed" && (
                          <div className="mt-1.5 space-y-1">
                            <div className="text-xs text-muted-foreground">
                              {s.method && <span>{METHOD_LABEL[s.method] ?? s.method}</span>}
                              {s.signedAt && <span> · {format(new Date(s.signedAt), "MMM d, yyyy h:mm a")}</span>}
                            </div>
                            {s.signedImageKey && state.approval && (
                              <AuthedImage
                                url={`/api/approvals/${state.approval.id}/signature/signers/${encodeURIComponent(s.email)}/image`}
                                getToken={getToken}
                                alt="Signature"
                                className="border rounded bg-white max-h-16"
                              />
                            )}
                            {s.consentIp && (
                              <div className="text-[10px] text-muted-foreground font-mono">IP {s.consentIp}</div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge
                          variant={s.status === "signed" ? "default" : s.status === "declined" ? "destructive" : "secondary"}
                          className={s.status === "signed" ? "bg-green-600" : ""}
                        >
                          {s.status}
                        </Badge>
                        {canSignInApp && (
                          <>
                            <Button size="sm" onClick={() => setSignDialogOpen(true)}>
                              <PenLine className="h-3.5 w-3.5 mr-1" /> Sign in app
                            </Button>
                            {state.approval && (
                              <Button asChild size="sm" variant="outline">
                                <Link href={`/sign/${state.approval.id}`}>
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open signer page
                                </Link>
                              </Button>
                            )}
                          </>
                        )}
                        {isAdmin && s.status !== "signed" && (
                          <>
                            {s.status === "sent" && allowed !== "in_app_only" && (
                              <Button size="sm" variant="outline" onClick={() => handleSimulateSign(s.email)}>
                                Simulate sign
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleRemoveSigner(s.email)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {session?.yourSigner && session.yourSigner.status !== "signed" && session.allowedSigningMethods === "in_app_only" && (
              <div className="text-xs text-muted-foreground bg-muted/40 rounded p-2">
                This organization requires in-app signing. Use the "Sign in app" button next to your name.
              </div>
            )}

            {state.signature?.signedPdfKey && state.approval && (
              <div className="flex gap-3 text-sm">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => void openAuthed(`/api/approvals/${state.approval!.id}/signature/signed-pdf`, getToken)}
                >
                  Open signed PDF
                </button>
                <span className="text-muted-foreground">·</span>
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => void openAuthed(`/api/approvals/${state.approval!.id}/signature/signed-pdf`, getToken, { download: `signed_contract_${state.approval!.contractId}.pdf` })}
                >
                  Download
                </button>
              </div>
            )}

            {state.approval && (
              <SignInAppDialog
                open={signDialogOpen}
                onOpenChange={setSignDialogOpen}
                approvalId={state.approval.id}
                defaultName={session?.yourSigner?.name}
                onSigned={() => { void refresh(); }}
              />
            )}

            {isAdmin && state.signature?.status !== "completed" && (
              <>
                <Separator />
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[180px]">
                    <Input placeholder="signer@email.com" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <Input placeholder="Name (optional)" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
                  </div>
                  <Button variant="outline" onClick={handleAddSigner}>Add signer</Button>
                  {state.signature && state.signature.status === "pending" && state.signature.signers.length > 0 && session?.allowedSigningMethods !== "in_app_only" && (
                    <Button onClick={handleSendEnvelope} className="bg-[#0B1834] hover:bg-[#0B1834]/90">
                      <Send className="h-4 w-4 mr-2" /> Send for signature
                    </Button>
                  )}
                </div>
                {session?.allowedSigningMethods === "in_app_only" && state.signature?.signers.length ? (
                  <p className="text-xs text-muted-foreground">
                    External signing is disabled. Signers must use the "Sign in app" button.
                  </p>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Decision history */}
      {state.steps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Decision History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {state.steps.map((s) => (
                <div key={s.id} className="py-3 first:pt-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium flex items-center gap-2">
                        {STAGE_LABEL[s.stage]}
                        <Badge
                          variant={s.decision === "approved" ? "default" : s.decision === "rejected" ? "destructive" : "outline"}
                          className={`text-[10px] ${s.decision === "approved" ? "bg-green-600" : ""}`}
                        >
                          {s.decision ?? "pending"}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {s.approverEmail}
                        {s.decidedAt && ` · ${format(new Date(s.decidedAt), "MMM d, yyyy h:mm a")}`}
                      </div>
                      {s.comment && (
                        <div className="text-sm mt-1.5 whitespace-pre-wrap bg-muted/50 rounded p-2 border-l-2 border-muted-foreground/30">
                          {s.comment}
                        </div>
                      )}
                      {s.annotations && s.annotations.length > 0 && (
                        <div className="mt-1.5 space-y-1" data-testid={`step-annotations-${s.id}`}>
                          <div className="text-[11px] font-medium text-muted-foreground">
                            Inline notes ({s.annotations.length})
                          </div>
                          {s.annotations.map((a, i) => (
                            <div
                              key={i}
                              className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1"
                            >
                              <span className="font-medium">{a.fieldLabel ?? a.field}:</span>{" "}
                              <span className="whitespace-pre-wrap">{a.comment}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Previous approval cycles */}
      {state.history.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Previous Approval Cycles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y text-xs">
              {state.history.slice(1).map((h) => (
                <div key={h.id} className="py-2 flex justify-between items-center">
                  <div>
                    <span className="font-medium">{h.isAmendment ? "Amendment" : "Approval"} #{h.id}</span>
                    <span className="text-muted-foreground ml-2">{h.status.replace(/_/g, " ")}</span>
                    {h.submittedBy && <span className="text-muted-foreground ml-2">by {h.submittedBy}</span>}
                  </div>
                  <div className="text-muted-foreground">
                    {h.submittedAt ? format(new Date(h.submittedAt), "MMM d, yyyy") : "—"}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
